/** Shared plan copy and helpers for Pricing + Billing settings. */

import { DEFAULT_LANDING_ACCENT, hexToRgb, rgbToTuple } from '../../utils/landingAccent';

/** Marketing / Essentials accent — aligned with app primary purple. */
export const ESSENTIALS_ACCENT = DEFAULT_LANDING_ACCENT;
const _essentialsRgb = hexToRgb(ESSENTIALS_ACCENT);
export const ESSENTIALS_ACCENT_RGB = _essentialsRgb ? rgbToTuple(_essentialsRgb) : '91, 108, 255';

export function essentialsRgba(alpha) {
  return `rgba(${ESSENTIALS_ACCENT_RGB}, ${alpha})`;
}

/** Pro tier premium gold — warm gold with a hint of orange (between legacy orange and yellow). */
export const PRO_GOLD = '#F0B040';
export const PRO_GOLD_DEEP = '#D49628';
export const PRO_GOLD_RGB = '240, 176, 64';

export function proGoldRgba(alpha) {
  return `rgba(${PRO_GOLD_RGB}, ${alpha})`;
}

export const PLAN_DISPLAY_NAMES = {
  track: 'PrecisionTrader Track',
  trade: 'PrecisionTrader Trade',
};

/** Short names shown on cards, settings, and badges. */
export const PLAN_SHORT_NAMES = {
  track: 'Track',
  trade: 'Trade',
};

export const TIER_RANK = { track: 0, trade: 1 };

/** Canonical subscription prices (USD). Track is free. */
export const PLAN_PRICES = {
  track: { monthly: 0, annual: 0 },
  trade: { monthly: 24.99, annual: 249.90 },
};

PLAN_DISPLAY_NAMES.essentials = PLAN_DISPLAY_NAMES.track;
PLAN_DISPLAY_NAMES.pro = PLAN_DISPLAY_NAMES.trade;
PLAN_SHORT_NAMES.essentials = PLAN_SHORT_NAMES.track;
PLAN_SHORT_NAMES.pro = PLAN_SHORT_NAMES.trade;
TIER_RANK.essentials = TIER_RANK.track;
TIER_RANK.pro = TIER_RANK.trade;
PLAN_PRICES.essentials = PLAN_PRICES.track;
PLAN_PRICES.pro = PLAN_PRICES.trade;

/** `essentials` / `pro` still resolve for older payloads. */
export function normalizePlanTier(tier) {
  if (tier === 'track' || tier === 'essentials') return 'track';
  if (tier === 'trade' || tier === 'pro') return 'trade';
  return null;
}

export function formatPlanPrice(amount) {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return amount.toFixed(2);
}

export function formatPlanPriceDisplay(amount) {
  return `$${formatPlanPrice(amount)}`;
}

export const BILLING_PLANS = {
  track: {
    tier: 'track',
    name: PLAN_SHORT_NAMES.track,
    displayName: PLAN_DISPLAY_NAMES.track,
    priceMonthly: PLAN_PRICES.track.monthly,
    priceAnnual: PLAN_PRICES.track.annual,
    accent: ESSENTIALS_ACCENT,
    accentSoft: essentialsRgba(0.12),
    border: essentialsRgba(0.45),
    darkCta: false,
    highlights: [
      'Trading rules that alert you',
      'Account overview & behavior analytics',
      'Watchlists, journals, and alerts',
      'Unlimited PrecisionAI',
    ],
  },
  trade: {
    tier: 'trade',
    name: PLAN_SHORT_NAMES.trade,
    displayName: PLAN_DISPLAY_NAMES.trade,
    priceMonthly: PLAN_PRICES.trade.monthly,
    priceAnnual: PLAN_PRICES.trade.annual,
    accent: PRO_GOLD,
    accentSoft: proGoldRgba(0.12),
    border: proGoldRgba(0.55),
    darkCta: true,
    badge: 'Stops the trade',
    goldHighlight: PRO_GOLD,
    highlights: [
      { text: 'Rules that block the order', gold: true },
      { text: 'Real-time trading and order execution', gold: true },
      { text: 'Auto position sizing based on risk settings' },
      { text: 'Live charts, indicators & order entry' },
      { text: 'Everything in Track' },
    ],
  },
};

BILLING_PLANS.essentials = BILLING_PLANS.track;
BILLING_PLANS.pro = BILLING_PLANS.trade;

/** Normalize string or { text, gold? } highlight entries. */
export function normalizeHighlight(item) {
  if (typeof item === 'string') return { text: item, gold: false };
  return { text: item.text, gold: !!item.gold };
}

export function getPlanActionLabel(currentTier, targetTier) {
  const current = normalizePlanTier(currentTier);
  const target = normalizePlanTier(targetTier);
  if (current && current === target) return 'Subscribe';
  const currentRank = TIER_RANK[current] ?? 0;
  const targetRank = TIER_RANK[target] ?? 0;
  if (targetRank > currentRank) return 'Upgrade';
  if (targetRank < currentRank) return 'Downgrade';
  return 'Switch plan';
}

/** @returns {'upgrade'|'downgrade'|'subscribe'} */
export function getPlanChangeType(currentTier, targetTier) {
  const currentRank = TIER_RANK[normalizePlanTier(currentTier)] ?? 0;
  const targetRank = TIER_RANK[normalizePlanTier(targetTier)] ?? 0;
  if (targetRank > currentRank) return 'upgrade';
  if (targetRank < currentRank) return 'downgrade';
  return 'subscribe';
}

export const CHECKOUT_CANCEL_RETURNS = ['pricing', 'onboarding', 'settings', 'flash-sale'];
export const SETTINGS_CHECKOUT_CANCEL_PATHS = ['/settings#billing', '/dashboard/settings#billing'];

/**
 * Stripe Checkout cancel destination from the page the user left.
 * @param {{ pathname?: string, hash?: string }} [location]
 */
export function getCheckoutCancelFromLocation(location) {
  const loc = location || (typeof window !== 'undefined' ? window.location : {});
  const pathname = loc.pathname || '';
  if (pathname.startsWith('/onboarding')) {
    return { cancelReturn: 'onboarding' };
  }
  if (pathname === '/settings' || pathname === '/dashboard/settings') {
    return { cancelReturn: 'settings', cancelPath: `${pathname}#billing` };
  }
  if (pathname === '/flash-sale' || pathname.startsWith('/flash-sale')) {
    return { cancelReturn: 'flash-sale' };
  }
  return { cancelReturn: 'pricing' };
}

const CHECKOUT_RETURN_KEY = 'pt_billing_checkout_return';

/** Remember plan change before Stripe redirect (pricing or settings billing). */
export function setBillingCheckoutReturn({ change, targetTier, source = 'pricing', returnPath }) {
  try {
    sessionStorage.setItem(
      CHECKOUT_RETURN_KEY,
      JSON.stringify({ source, change, targetTier, returnPath })
    );
  } catch (_) {}
}

/** @returns {{ source: 'pricing'|'settings', change: string, targetTier: string, returnPath?: string } | null} */
export function consumeBillingCheckoutReturn() {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_RETURN_KEY);
    sessionStorage.removeItem(CHECKOUT_RETURN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/** URL flag on /trade after a Pro upgrade (SubscriptionSuccess + trial upgrade). */
export const PRO_WELCOME_QUERY_KEY = 'welcome';
export const PRO_WELCOME_QUERY_VALUE = 'trade';

const PRO_WELCOME_SESSION_PREFIX = 'pt_pro_welcome_dismissed_';

/** @returns {string} */
export function buildTradePathWithProWelcome() {
  return `/trade?${PRO_WELCOME_QUERY_KEY}=${PRO_WELCOME_QUERY_VALUE}`;
}

/** @returns {boolean} */
export function shouldShowProWelcomeAfterCheckout(ctx) {
  if (
    !ctx ||
    (ctx.source !== 'pricing' && ctx.source !== 'settings' && ctx.source !== 'flash-sale') ||
    normalizePlanTier(ctx.targetTier) !== 'trade'
  ) {
    return false;
  }
  if (ctx.change === 'downgrade') return false;
  if (ctx.change === 'upgrade') return true;
  return normalizePlanTier(ctx.targetTier) === 'trade';
}

export function getProWelcomeSessionKey(userId) {
  return `${PRO_WELCOME_SESSION_PREFIX}${userId ?? 'anon'}`;
}

export function markProWelcomeDismissed(userId) {
  try {
    sessionStorage.setItem(getProWelcomeSessionKey(userId), '1');
  } catch (_) {}
}

export function wasProWelcomeDismissed(userId) {
  try {
    return sessionStorage.getItem(getProWelcomeSessionKey(userId)) === '1';
  } catch (_) {
    return false;
  }
}

/** Post-checkout destination after a pricing- or settings-page plan change. */
export function getPricingCheckoutDestination(ctx) {
  if (!ctx) return null;
  const { source } = ctx;
  if (source === 'flash-sale') {
    return shouldShowProWelcomeAfterCheckout(ctx) ? buildTradePathWithProWelcome() : '/trade';
  }
  if (source !== 'pricing' && source !== 'settings') return null;

  const stayDest = source === 'settings' ? ctx.returnPath || '/settings#billing' : '/pricing';

  if (ctx.change === 'upgrade') {
    return shouldShowProWelcomeAfterCheckout(ctx) ? buildTradePathWithProWelcome() : '/trade';
  }
  if (ctx.change === 'downgrade') return stayDest;
  return shouldShowProWelcomeAfterCheckout(ctx) ? buildTradePathWithProWelcome() : stayDest;
}

export function getPlanPriceForInterval(plan, billingInterval = 'monthly') {
  return billingInterval === 'annual' ? plan.priceAnnual : plan.priceMonthly;
}

export function getPlanPriceSuffix(billingInterval = 'monthly') {
  return billingInterval === 'annual' ? '/year' : '/month';
}

/** Annual vs 12× monthly — used for “2 months free” / % off messaging. */
export function getAnnualSavings(plan) {
  const yearlyIfMonthly = plan.priceMonthly * 12;
  const saved = Math.max(0, yearlyIfMonthly - plan.priceAnnual);
  const percentOff = yearlyIfMonthly > 0 ? Math.round((saved / yearlyIfMonthly) * 100) : 0;
  const monthsFree = plan.priceMonthly > 0 ? Math.round(saved / plan.priceMonthly) : 0;
  const effectiveMonthly = plan.priceAnnual / 12;
  return { saved, percentOff, monthsFree, effectiveMonthly, yearlyIfMonthly };
}
